import { useState, useEffect } from 'react';

type CookieConsent = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
};

type CookieConsentHook = {
  consent: CookieConsent;
  hasConsent: (type: keyof CookieConsent) => boolean;
  updateConsent: (updates: Partial<CookieConsent>) => void;
  showBanner: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: () => void;
};

export function useCookieConsent(): CookieConsentHook {
  const [showBanner, setShowBanner] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem('cookieConsent');
    if (savedConsent) {
      setConsent(JSON.parse(savedConsent));
    } else {
      setShowBanner(true);
    }
  }, []);

  const updateConsent = (updates: Partial<CookieConsent>) => {
    const newConsent = { ...consent, ...updates };
    setConsent(newConsent);
  };

  const saveToStorage = (consentData: CookieConsent) => {
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    // Set a cookie that expires in 1 year
    document.cookie = `cookieConsent=${JSON.stringify(consentData)}; path=/; max-age=31536000; SameSite=Lax`;
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setConsent(allAccepted);
    saveToStorage(allAccepted);
    setShowBanner(false);
  };

  const rejectAll = () => {
    const onlyNecessary = {
      necessary: true, // Necessary cookies cannot be rejected
      analytics: false,
      marketing: false,
      preferences: false,
    };
    setConsent(onlyNecessary);
    saveToStorage(onlyNecessary);
    setShowBanner(false);
  };

  const savePreferences = () => {
    saveToStorage(consent);
    setShowBanner(false);
  };

  const hasConsent = (type: keyof CookieConsent): boolean => {
    return consent[type];
  };

  return {
    consent,
    hasConsent,
    updateConsent,
    showBanner,
    acceptAll,
    rejectAll,
    savePreferences,
  };
}
